import moment from "moment";
import { initialize, change } from "redux-form/immutable";

import {
  ACTIONS,
  HELI_REPORT_FORM,
  WORK_SCHEDULE_REPORT_FORM,
} from "features/planner/report/report.constants";

import {
  editHeliReportFormSelector,
  getHeliReportGroupFromState,
  getWorkScheduleReportFromState,
} from "features/planner/report/report.selectors";

import {
  getWorkScheduleReportEndDate,
  getWorkScheduleReportStartDate,
} from "utils/report.util";

import dashboardService from "services/dashboard.service";
import reportService from "services/report.service";
import { getFormValuesFromState } from "app/app.selectors";
import { generateFormName, makeActionCreator, saveFile } from "utils/app.util";
import { getGroups, getEmployeeGroups } from "features/planner/planner.actions";
import { HeliReportEmployeeMapper } from "features/planner/report/report.mappers";

/**
 * POB
 */
export const getPOBReport = makeActionCreator({
  type: ACTIONS.GET_POB_REPORT,
  payload: reportService.getPOBReport,
});

/**
 * Heli
 */
export const getHeliReport = makeActionCreator({
  type: ACTIONS.GET_HELI_REPORT,
  payload: reportService.getHeliReport,
});

export const heliReportOnLoad = () => dispatch => {
  return dispatch(getGroups(false));
};

export const heliReportGroupOnLoad = groupId => (dispatch, getState) => {
  return dispatch(getHeliReport(groupId)).then(() => {
    const heliReport = getHeliReportGroupFromState(getState(), groupId);

    dispatch(initialize(HELI_REPORT_FORM.ID, heliReport));
  });
};

export const addHeliReportItem = () => (dispatch, getState) => {
  const state = getState();

  [HELI_REPORT_FORM.OFFSHORE_BOUND, HELI_REPORT_FORM.SHORE_BOUND].forEach(
    name => {
      const value = editHeliReportFormSelector(state, name);

      dispatch(
        change(
          HELI_REPORT_FORM.ID,
          name,
          value.push(HeliReportEmployeeMapper.initial({ index: value.size })),
        ),
      );
    },
  );

  dispatch(
    change(
      HELI_REPORT_FORM.ID,
      HELI_REPORT_FORM.MAX_INDEX,
      editHeliReportFormSelector(state, HELI_REPORT_FORM.MAX_INDEX) + 1,
    ),
  );
};

export const heliReportOnSubmit = heliReport => dispatch => {
  const payload = reportService
    .downloadHeliReport(heliReport)
    .then(([file, fileName]) => saveFile(file, fileName));

  dispatch({
    payload,
    type: ACTIONS.DOWNLOAD_HELI_REPORT,
  });

  return payload;
};

export const heliReportClearRow = (type, index) => dispatch => {
  dispatch(
    change(
      HELI_REPORT_FORM.ID,
      generateFormName(type, index),
      HeliReportEmployeeMapper.initial({ index }),
    ),
  );
};

/**
 * Work Schedule
 */
export const getWorkScheduleReport = makeActionCreator(
  {
    type: ACTIONS.GET_WORK_SCHEDULE_REPORT,
    payload: reportService.getWorkScheduleReport,
  },
  "params",
);

export const unloadWorkScheduleReport = makeActionCreator({
  type: ACTIONS.UNLOAD_WORK_SCHEDULE_REPORT,
  payload: () => Promise.resolve(),
});

export const workScheduleReportFormOnChange = (
  reportParams,
  dispatch,
  props,
) => {
  if (!props.dirty) return;

  const reportType = reportParams.get(WORK_SCHEDULE_REPORT_FORM.TYPE);

  const date = moment({
    year: reportParams.get(WORK_SCHEDULE_REPORT_FORM.YEAR),
    month: reportParams.get(WORK_SCHEDULE_REPORT_FORM.MONTH),
  });

  const startDate = getWorkScheduleReportStartDate(reportType, date.clone());
  const endDate = getWorkScheduleReportEndDate(reportType, date.clone());

  reportParams = reportParams
    .set(WORK_SCHEDULE_REPORT_FORM.START_DATE, startDate)
    .set(WORK_SCHEDULE_REPORT_FORM.END_DATE, endDate);

  dispatch(initialize(WORK_SCHEDULE_REPORT_FORM.ID, reportParams));

  return dispatch(getWorkScheduleReport(reportParams.toJS()));
};

export const workScheduleReportOnLoad = () => (dispatch, getState) => {
  const initialParams = getWorkScheduleReportFromState(getState()).get(
    "parameters",
  );

  dispatch(initialize(WORK_SCHEDULE_REPORT_FORM.ID, initialParams));

  return Promise.all([
    dispatch(getEmployeeGroups()),
    dispatch(getWorkScheduleReport(initialParams)),
  ]);
};

export const downloadScheduleReport = () => (dispatch, getState) => {
  const params = getFormValuesFromState(
    getState(),
    WORK_SCHEDULE_REPORT_FORM.ID,
  );

  const payload = reportService
    .downloadWorkScheduleReport(params.toJS())
    .then(([file, fileName]) => saveFile(file, fileName));

  dispatch({
    payload,
    type: ACTIONS.DOWNLOAD_WORK_SCHEDULE_REPORT,
  });

  return payload;
};

/**
 * Schedule Overrides Report
 */

export const getScheduleOverridesReport = makeActionCreator({
  type: ACTIONS.GET_SCHEDULE_OVERRIDES_REPORT,
  payload: reportService.getScheduleOverridesReport,
});

/**
 * Dashboard
 */
export const getDashboardStats = makeActionCreator({
  type: ACTIONS.GET_DASHBOARD_REPORT_STATS,
  payload: dashboardService.getDashboardStats,
});

export const getWorkScheduleOverrideTimeSeries = makeActionCreator({
  type: ACTIONS.GET_WORK_SCHEDULE_OVERRIDE_TIMESERIES,
  payload: dashboardService.getWorkScheduleOverrideTimeSeries,
});

export const dashboardOnLoad = () => dispatch => {
  const payload = Promise.all([
    dispatch(
      getWorkScheduleOverrideTimeSeries(moment().add(-6, "month"), moment()),
    ),
    dispatch(getDashboardStats()),
  ]);

  return payload;
};

export const getAvailablePersonnelOnshoreByCountry = makeActionCreator(
  {
    type: ACTIONS.GET_AVAILABLE_PERSONNEL_ONSHORE_BY_COUNTRY,
    payload: dashboardService.getAvailablePersonnelOnshoreByCountry,
  },
  "countryTwoLetterCode",
);

export const getPersonnelOnshoreWithRequiredTimeoffByCountry = makeActionCreator(
  {
    type: ACTIONS.GET_PERSONNEL_ONSHORE_WITH_REQUIRED_TIMEOFF_BY_COUNTRY,
    payload: dashboardService.getPersonnelOnshoreWithRequiredTimeoffByCountry,
  },
  "countryTwoLetterCode",
);

export const getEmployeesByEmployeeTypeAndCardContainerType = makeActionCreator(
  {
    type: ACTIONS.GET_EMPLOYEES_BY_EMPLOYEE_TYPE_AND_CARD_CONTAINER_TYPE,
    payload: dashboardService.getEmployeesByEmployeeTypeAndCardContainerType,
  },
);
